import AddFollowupTaskDialog, {
  AddFollowupTaskDialogField,
} from "../components/AddFollowupTaskDialog";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Switch,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import ConversationEntry, {
  getConversationElementId,
} from "../components/ConversationEntry";
import { PaneType, SelectionSource } from "../features/api/logging";
import SelfTestEntry, {
  getSelfTestElementId,
} from "../components/SelfTestEntry";
import TaskEntry, {
  TaskEditingState,
  getTaskElementId,
} from "../components/TaskEntry";
import {
  TaskPatientData,
  TaskState,
  TaskType,
  isTaskTypeSelfTestConcern,
  useAddPatientNoteMutation,
  useAddTaskNoteMutation,
  useCompleteTaskMutation,
  useCreateFollowUpTaskMutation,
  useDeleteTaskMutation,
  useGetPatientDetailQuery,
  useLazyGetPatientNotesQuery,
  useLazyGetPatientProfileQuery,
  useSetClinicianWorkingStateMutation,
} from "../features/data/patientApiSlice";
import {
  ViewingMode,
  selectChatOpenUxState,
  selectHasUnsavedNote,
  selectViewingMode,
  setChatOpenUxState,
  setViewingMode,
} from "../features/appState/uxSlice";
import { createStyleSheet, globalStyles } from "../utils/styles";
import { getErrorComponent, getErrorText } from "../utils/errors";
import {
  selectShowActiveOrRecentState,
  setShowActiveOrRecent,
} from "../features/appState/userPrefsSlice";
import { store, tabId, useAppDispatch } from "../features/store";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import AddTaskIcon from "@mui/icons-material/Add";
import ChatUx from "../components/ChatUx";
import DeleteTaskDialog from "../components/DeleteTaskDialog";
import ErrorDialog from "../components/ErrorDialog";
import IconButton from "../components/IconButton";
import MessagesIcon from "../../assets/messages.svg?react";
import Notes from "../components/Notes";
import PatientCollectedData from "../components/PatientCollectedData";
import PatientHeader from "../components/PatientHeader";
import React from "react";
import TabView from "../components/TabView";
import UnsavedChangesDialog from "../components/UnsavedChangesDialog";
import { formatDistance } from "date-fns";
import { isElementVisible } from "../utils/elements";
import { logEvent } from "../features/api/apiSlice";
import { theme } from "../../theme";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const WORKING_STATE_EXPIRY_SEC = 60;
const WORKING_STATE_RENEWAL_MS = 30000;
const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;
const FIND_ELEMENT_INTERVAL_MS = 100;
const FIND_ELEMENT_MAX_TRIES = 8;
const VISIBILITY_MAX_CHECKS = 4;
const VISIBILITY_TOLERANCE_TOP = -12;
const VISIBILITY_TOLERANCE_BOTTOM = 64;

const Patient = () => {
  const params = useParams();
  const uid = params.uid || "";
  const theme = useTheme();
  const location = useLocation();
  const navigationSelectedTaskUid = location.state?.taskUid || "";
  const { t } = useTranslation("Patient");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const chatOpen = useSelector(selectChatOpenUxState);
  const showActiveOrRecent = useSelector(selectShowActiveOrRecentState);
  const viewingMode = useSelector(selectViewingMode);
  const taskListScrollRef = useRef<HTMLDivElement>(null);
  
  // Queries/mutations
  const {
    data: patient,
    isFetching,
    isError,
    error,
  } = useGetPatientDetailQuery({ uid }, { skip: !uid?.trim() });
  const [setClinicianWorkingState] = useSetClinicianWorkingStateMutation({});
  const [createFollowUpTask] = useCreateFollowUpTaskMutation();
  const [addPatientNote] = useAddPatientNoteMutation();
  const [addTaskNote] = useAddTaskNoteMutation();
  const [completeTask] = useCompleteTaskMutation();
  const [deleteTask] = useDeleteTaskMutation();
  const [getPatientNotes, patientNotesResult] = useLazyGetPatientNotesQuery();
  const [getPatientProfile, patientProfileResult] =
  useLazyGetPatientProfileQuery();
  const prevUidRef = useRef<string>("");
  
  // Component state
  const [selectedTaskUid, setSelectedTaskUid] = useState(
    navigationSelectedTaskUid
  );
  const [hasFetched, setHasFetched] = useState(false);
  const [needToScrollToTask, setNeedToScrollToTask] = useState(false);
  const [elementIdToExpand, setElementIdToExpand] = useState("");
  const [deleteTaskDialogInfo, setDeleteTaskDialogInfo] = useState<{
    open: boolean;
    taskUid?: string;
    taskType?: TaskType;
    taskCreated?: string;
  }>({ open: false });
  const [errorDialogInfo, setErrorDialogInfo] = useState<{
    message?: string;
    open: boolean;
  }>({ open: false });
  const [addFollowUpTaskDialogOpen, setAddFollowUpTaskDialogOpen] =
    useState(false);
  const [unsavedChangesDialogInfo, setUnsavedChangesDialogInfo] = useState<{
    open: boolean;
    viewingMode?: ViewingMode;
  }>({ open: false });
  const [taskEditingStateMap, setTaskEditingStateMap] = useState(
    new Map<string, TaskEditingState>()
  );
  const updateTaskEditingState = useCallback(
    (taskUid: string, taskEditingState: TaskEditingState) =>
      setTaskEditingStateMap(
        new Map<string, TaskEditingState>(
          taskEditingStateMap.set(taskUid, taskEditingState)
        )
      ),
    [taskEditingStateMap]
  );
  const [nudgeData, setNudgeData] = useState<{
    numNudgesSinceLastClientMessage: number;
    minNudgeMessageAge: number;
  }>({
    numNudgesSinceLastClientMessage: 0,
    minNudgeMessageAge: 0,
  });

  // Reactive code
  useEffect(() => {
    // Selecting a new patient means show fetch/spinner
    setHasFetched(false);
  }, [uid]);
  useEffect(() => {
    // Selecting a new task means we should scroll to it
    if (navigationSelectedTaskUid) {
      setSelectedTaskUid(navigationSelectedTaskUid);
      setNeedToScrollToTask(true);
    }
  }, [navigationSelectedTaskUid]);
  useEffect(() => {
    // If no task was selected via navigation or it's one that won't be
    // displayed, and if there isn't any selected task yet, select the first
    // task once we get some data
    if (
      ((!navigationSelectedTaskUid && !selectedTaskUid) ||
        (selectedTaskUid &&
          !patient?.tasks.find((t) => t.uid === selectedTaskUid))) &&
      patient?.displayItems.length
    ) {
      // No suitable task found; try to select the first conversation or
      // self-test instead
      const firstDisplayItem = patient.displayItems[0];
      if (firstDisplayItem.task) {
        setSelectedTaskUid(firstDisplayItem.task.uid);
      } else if (firstDisplayItem.conversation) {
        setElementIdToExpand(
          getConversationElementId(firstDisplayItem.conversation.uid)
        );
      } else if (firstDisplayItem.self_test) {
        setElementIdToExpand(
          getSelfTestElementId(firstDisplayItem.self_test.uid)
        );
      }
      setNeedToScrollToTask(true);
    }
  }, [
    navigationSelectedTaskUid,
    selectedTaskUid,
    patient?.tasks,
    patient?.displayItems,
  ]);
  useEffect(() => {
    // Finishing the initial fetch means we don't need to show the spinner
    // again until a new patient gets selected (see above)
    if (!isFetching) {
      setHasFetched(true);
    }
  }, [isFetching]);

  useEffect(() => {
    const canCallMutation = () => window.navigator.onLine;

    let intervalId: number | undefined;
    if (uid) {
      const renewWorkingState = () => {
        if (canCallMutation()) {
          console.log(
            "Renewing working state for " + uid + " from instance " + tabId
          );
          setClinicianWorkingState({
            patientUid: uid,
            expirationSeconds: WORKING_STATE_EXPIRY_SEC,
          });
        }
      };

      renewWorkingState();
      intervalId = window.setInterval(
        renewWorkingState,
        WORKING_STATE_RENEWAL_MS
      );
    }
    if (prevUidRef.current && prevUidRef.current !== uid && canCallMutation()) {
      console.log("Releasing working state for " + prevUidRef.current);
      setClinicianWorkingState({
        patientUid: prevUidRef.current,
        expirationSeconds: 0,
      });
    }

    prevUidRef.current = uid;

    return () => {
      if (typeof intervalId !== "undefined") {
        clearInterval(intervalId);
      }
    };
  }, [uid, setClinicianWorkingState]);

  useEffect(() => {
    // When we need to scroll to a task, figure out what element to scroll to
    if (needToScrollToTask) {
      const taskToSelect = patient?.tasks.find(
        (task) => task.uid === selectedTaskUid
      );
      if (taskToSelect) {
        const eidToExpand =
          (taskToSelect.self_test_uid &&
            getSelfTestElementId(taskToSelect.self_test_uid)) ||
          (taskToSelect.conversation_uid &&
            getConversationElementId(taskToSelect.conversation_uid)) ||
          getTaskElementId(taskToSelect.uid, true);
        setElementIdToExpand(eidToExpand);
        setNeedToScrollToTask(false);
      }
    }
  }, [patient?.tasks, selectedTaskUid, needToScrollToTask]);
  useEffect(() => {
    // Handle scrolling to the task element
    if (elementIdToExpand) {
      let numFindTries = 0,
        numVisibilityChecks = 0;
      let foundElement: HTMLElement | null = null;
      const intervalId = window.setInterval(() => {
        if (numVisibilityChecks === 0) {
          numFindTries++;
          // Search for the element each interval in case it was re-rendered -
          // this can happen if the task gets auto-expanded
          const element = document.getElementById(
            getTaskElementId(selectedTaskUid, true)
          );
          if (element) {
            foundElement = element;
            // Do we need to scroll?
            if (
              !isElementVisible(foundElement, taskListScrollRef.current!, {
                top: VISIBILITY_TOLERANCE_TOP,
                bottom: VISIBILITY_TOLERANCE_BOTTOM,
              })
            ) {
              // Try scrolling to the element
              element.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
              numVisibilityChecks = VISIBILITY_MAX_CHECKS;
            } else {
              // No need to scroll
              clearInterval(intervalId);
            }
          } else if (numFindTries > FIND_ELEMENT_MAX_TRIES) {
            console.log("GIVING UP ON FINDING ELEMENT");
            // Element not found; possibly a task type that's not rendered
            clearInterval(intervalId);
          }
        } else {
          // Check if the element is visible yet (scrolling might not do much
          // due to rendering/layout still stabilizing and might need to get
          // tried again)
          if (
            isElementVisible(foundElement, taskListScrollRef.current!, {
              top: VISIBILITY_TOLERANCE_TOP,
              bottom: VISIBILITY_TOLERANCE_BOTTOM,
            })
          ) {
            // Great! Scrolling can be considered complete
            clearInterval(intervalId);
          } else {
            // Check again in a bit
            numVisibilityChecks--;
            numFindTries = 0;
          }
        }
      }, FIND_ELEMENT_INTERVAL_MS);
      return () => {
        clearInterval(intervalId);
      };
    }
  }, [elementIdToExpand, selectedTaskUid]);
  useEffect(() => {
    // When the notes view is chosen, load the notes
    if (viewingMode === ViewingMode.Notes) {
      getPatientNotes({ uid }, true);
    } else if (viewingMode === ViewingMode.CollectedData) {
      // When the collected data view is chosen, load the data
      getPatientProfile({ uid }, true);
    }
  }, [viewingMode, getPatientNotes, getPatientProfile, uid]);

  // Callbacks
  const changeViewingMode = useCallback(
    (mode: ViewingMode) => {
      logEvent(dispatch, {
        event: "patientDetailViewingModeChanged",
        data: { viewingMode: mode },
      });
      dispatch(setViewingMode(mode));
    },
    [dispatch]
  );
  const handleViewingModeTabClicked = useCallback(
    (mode: ViewingMode) => () => {
      // Switching tabs unmounts the note inputs; chat stays mounted.
      if (mode !== viewingMode && selectHasUnsavedNote(store.getState())) {
        setUnsavedChangesDialogInfo({ open: true, viewingMode: mode });
      } else {
        changeViewingMode(mode);
      }
    },
    [changeViewingMode, viewingMode]
  );
  const handleUnsavedChangesDialogClose = useCallback(() => {
    setUnsavedChangesDialogInfo({ open: false });
  }, []);
  const handleUnsavedChangesDialogDiscard = useCallback(() => {
    setUnsavedChangesDialogInfo({ open: false });
    changeViewingMode(unsavedChangesDialogInfo.viewingMode!);
  }, [changeViewingMode, unsavedChangesDialogInfo]);
  const handleShowOnlyActiveOrRecentChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      logEvent(dispatch, {
        event: "showOnlyRecentAndActiveSelected",
        data: { selected: checked },
      });
      dispatch(setShowActiveOrRecent(checked));
    },
    [dispatch]
  );
  const handleAddFollowUpTaskClick = useCallback(() => {
    setAddFollowUpTaskDialogOpen(true);
  }, []);
  const handleAddFollowUpTaskDialogClose = useCallback(() => {
    setAddFollowUpTaskDialogOpen(false);
  }, []);
  const handleAddFollowUpTaskDialogValidateFields = useCallback(
    (taskData: {
      title: string;
      description: string;
      dueDate: Date | null;
    }):
      | { field?: AddFollowupTaskDialogField; errorText?: string }
      | undefined => {
      // Don't allow saving if the title is empty
      if (!taskData.title.trim()) {
        return {
          field: AddFollowupTaskDialogField.title,
          errorText: t("titleRequired"),
        };
      }
    },
    [t]
  );
  const handleAddFollowUpTaskDialogSave = useCallback(
    async (taskData: {
      title: string;
      description: string;
      dueDate: Date | null;
    }) => {
      if (taskData.title.trim()) {
        updateTaskEditingState("*", TaskEditingState.saving);
        const result = await createFollowUpTask({
          patientUid: uid,
          title: taskData.title,
          description: taskData.description,
          dueDate: taskData.dueDate?.toISOString() || undefined,
        });
        taskEditingStateMap.delete("*");
        setAddFollowUpTaskDialogOpen(false);
        logEvent(dispatch, {
          event: "taskAdded",
          data: { taskUid: (result as any).data || "error" },
        });
      }
    },
    [
      createFollowUpTask,
      updateTaskEditingState,
      uid,
      taskEditingStateMap,
      dispatch,
    ]
  );
  const handleSelectTask = useCallback(
    (taskUid: string) => {
      if (selectedTaskUid !== taskUid) {
        logEvent(dispatch, {
          event: "taskSelected",
          data: { taskUid, selectionSource: SelectionSource.PatientDetail },
        });
        setSelectedTaskUid(taskUid);
      }
    },
    [selectedTaskUid, dispatch]
  );
  const handleAddTaskNote = useCallback(
    (taskUid: string, content: string) => {
      if (content) {
        logEvent(dispatch, {
          event: "taskNoteAdded",
          data: { taskUid },
        });
        addTaskNote({ patientUid: uid, taskUid, content });
      }
    },
    [addTaskNote, uid, dispatch]
  );
  const handleCompleteTaskDialogAction = useCallback(
    async (completeData: { actionsTaken: string[]; notes: string }, task: TaskPatientData) => {
      updateTaskEditingState(
        task.uid!,
        TaskEditingState.saving
      );
      await completeTask({
        patientUid: uid,
        taskUid: task.uid!,
        actionsTaken: completeData.actionsTaken,
        noteContent: completeData.notes,
      });
      updateTaskEditingState(
        task.uid!,
        TaskEditingState.done
      );
      logEvent(dispatch, {
        event: "taskCompleted",
        data: {
          taskUid: task.uid!,
          numActionsTaken: completeData.actionsTaken.length,
          hasNotes: completeData.notes.length > 0,
        },
      });
    },
    [
      updateTaskEditingState,
      completeTask,
      uid,
      dispatch,
    ]
  );
  const handleCompleteTask = useCallback(
    (completeData: { actionsTaken: string[]; notes: string }, task: TaskPatientData) => {
      // Check if a self-test concern task has an interpretation
      if (isTaskTypeSelfTestConcern(task.type) && task.self_test_uid) {
        const associatedSelfTest = patient?.displayItems.find(
          ({ self_test }) => self_test?.uid === task.self_test_uid
        )?.self_test;
        if (
          !associatedSelfTest?.image_reviewer_interpretation &&
          associatedSelfTest?.num_photos !== 0
        ) {
          // Can't complete a self-test concern task without an interpretation
          // (unless this test doesn't have any photos)
          setErrorDialogInfo({
            message: t("interpretationRequired"),
            open: true,
          });
          return;
        }
      }
      handleCompleteTaskDialogAction(completeData, task);
    },
    [patient?.displayItems, t, handleCompleteTaskDialogAction]
  );
  const handleDeleteTask = useCallback((task: TaskPatientData) => {
    setDeleteTaskDialogInfo({
      open: true,
      taskUid: task.uid,
      taskType: task.type,
      taskCreated: task.created,
    });
  }, []);
  const handleDeleteTaskDialogClose = useCallback(() => {
    setDeleteTaskDialogInfo({ open: false });
  }, []);
  const handleDeleteTaskDialogAction = useCallback(
    async (deleteData: { notes: string }) => {
      setDeleteTaskDialogInfo({ open: false });
      updateTaskEditingState(
        deleteTaskDialogInfo.taskUid!,
        TaskEditingState.saving
      );
      await deleteTask({
        patientUid: uid,
        taskUid: deleteTaskDialogInfo.taskUid!,
        noteContent: deleteData.notes,
      });
      updateTaskEditingState(
        deleteTaskDialogInfo.taskUid!,
        TaskEditingState.done
      );
      logEvent(dispatch, {
        event: "taskDeleted",
        data: {
          taskUid: deleteTaskDialogInfo.taskUid!,
          hasNotes: deleteData.notes.length > 0,
        },
      });
    },
    [updateTaskEditingState, deleteTask, deleteTaskDialogInfo, uid, dispatch]
  );
  const handleAddPatientNote = useCallback(
    (content: string) => {
      if (content) {
        logEvent(dispatch, {
          event: "patientNoteAdded",
          data: { patientUid: uid },
        });
        addPatientNote({ uid, content });
      }
    },
    [addPatientNote, uid, dispatch]
  );
  const handleChatClick = useCallback(() => {
    logEvent(dispatch, {
      event: "paneOpened",
      data: { pane: PaneType.ChatUx },
    });
    dispatch(setChatOpenUxState(true));
  }, [dispatch]);
  const updateNudgeData = useCallback(
    (numNudgesSinceLastClientMessage: number, minNudgeMessageAge: number) => {
      setNudgeData({
        numNudgesSinceLastClientMessage,
        minNudgeMessageAge,
      });
    },
    []
  );

  const isDateRecent = (date: string) =>
    Date.now() - Date.parse(date) < THREE_DAYS_MS;
  const shouldShowTask = (task: TaskPatientData) =>
    !showActiveOrRecent ||
    task.state === TaskState.todo ||
    (task.state !== TaskState.deleted &&
      (isDateRecent(task.created) ||
        (task.due_date && isDateRecent(task.due_date))));
  const renderDisplayItems = () => {
    if (!patient) {
      return null;
    }
    const items = patient.displayItems.map(
      ({ task, self_test, conversation }) =>
        task
          ? shouldShowTask(task) && (
              <TaskEntry
                key={task.uid}
                task={task}
                expandByDefault={true}
                editingState={taskEditingStateMap.get(task.uid)}
                selected={task.uid === selectedTaskUid}
                onSelect={handleSelectTask}
                onAddNote={handleAddTaskNote}
                onComplete={handleCompleteTask}
                onDelete={handleDeleteTask}
              />
            )
          : self_test
          ? (!showActiveOrRecent ||
              isDateRecent(self_test.timestamp) ||
              patient.tasks.some(
                (task) =>
                  task.self_test_uid === self_test.uid && shouldShowTask(task)
              )) && (
              <SelfTestEntry
                key={self_test.uid}
                selfTestData={self_test}
                expandByDefault={
                  elementIdToExpand === getSelfTestElementId(self_test.uid)
                }
                tasks={patient.tasks.filter(
                  (task) =>
                    task.self_test_uid === self_test.uid && shouldShowTask(task)
                )}
                patientUid={patient.uid}
                selectedTaskUid={selectedTaskUid}
                onSelectTask={handleSelectTask}
                onAddTaskNote={handleAddTaskNote}
                onCompleteTask={handleCompleteTask}
                onDeleteTask={handleDeleteTask}
              />
            )
          : conversation
          ? (!showActiveOrRecent ||
              isDateRecent(conversation.timestamp) ||
              patient.tasks.some(
                (task) =>
                  task.conversation_uid === conversation.uid &&
                  shouldShowTask(task)
              )) && (
              <ConversationEntry
                key={conversation.uid}
                conversationData={conversation}
                expandByDefault={
                  elementIdToExpand ===
                  getConversationElementId(conversation.uid)
                }
                tasks={patient.tasks.filter(
                  (task) =>
                    task.conversation_uid === conversation.uid &&
                    shouldShowTask(task)
                )}
                selectedTaskUid={selectedTaskUid}
                onSelectTask={handleSelectTask}
                onAddTaskNote={handleAddTaskNote}
                onCompleteTask={handleCompleteTask}
                onDeleteTask={handleDeleteTask}
              />
            )
          : null
    );

    if (items.some((x) => !!x)) {
      return items;
    } else {
      return (
        <Typography variant="body1" sx={globalStyles.gapTop}>
          {showActiveOrRecent
            ? t("noItemsToShow")
            : t("noConversationsOrTasks")}
        </Typography>
      );
    }
  };
  return (
    <Box sx={styles.container}>
      {isFetching &&
      !hasFetched &&
      taskEditingStateMap.get("*") !== TaskEditingState.saving ? ( // Don't show progress spinner if creating follow-up task
        <Box sx={styles.progressContainer}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        getErrorComponent(error, navigate)
      ) : patient ? (
        <>
          <Box sx={styles.subContainer}>
            <Box sx={styles.detailContainer}>
              <PatientHeader patient={patient} />
              <Box sx={styles.taskListContainer} ref={taskListScrollRef}>
                <TabView
                  entries={[
                    {
                      content: t("tasks"),
                      selected: viewingMode === ViewingMode.Tasks,
                      onClick: handleViewingModeTabClicked(ViewingMode.Tasks),
                    },
                    {
                      content: t("notes"),
                      selected: viewingMode === ViewingMode.Notes,
                      onClick: handleViewingModeTabClicked(ViewingMode.Notes),
                    },
                    {
                      content: t("collectedData"),
                      selected: viewingMode === ViewingMode.CollectedData,
                      onClick: handleViewingModeTabClicked(
                        ViewingMode.CollectedData
                      ),
                    },
                  ]}
                />
                {viewingMode === ViewingMode.Tasks ? (
                  <Stack direction="column">
                    <Stack direction="row" sx={styles.showActiveRow}>
                      <Typography variant="button">
                        {t("showActiveOrRecent")}
                      </Typography>
                      <Switch
                        checked={showActiveOrRecent}
                        onChange={handleShowOnlyActiveOrRecentChange}
                      />
                      <Box sx={globalStyles.flexSpace} />
                      <Button
                        variant="outlined"
                        onClick={handleAddFollowUpTaskClick}
                      >
                        <AddTaskIcon fontSize="small" sx={styles.iconGap} />
                        {t("addNewTask")}
                      </Button>
                    </Stack>
                    {renderDisplayItems()}
                  </Stack>
                ) : viewingMode === ViewingMode.Notes ? (
                  patientNotesResult.isLoading ? (
                    <Box sx={styles.progressContainer}>
                      <CircularProgress />
                    </Box>
                  ) : patientNotesResult.isError ? (
                    <Typography variant="errorBody">
                      {getErrorText(patientNotesResult.error)}
                    </Typography>
                  ) : (
                    <Notes
                      notes={patientNotesResult.data?.notes || []}
                      onAddNote={handleAddPatientNote}
                    />
                  )
                ) : viewingMode === ViewingMode.CollectedData ? (
                  patientProfileResult.isLoading ? (
                    <Box sx={styles.progressContainer}>
                      <CircularProgress />
                    </Box>
                  ) : patientProfileResult.isError ? (
                    <Typography variant="errorBody">
                      {getErrorText(patientProfileResult.error)}
                    </Typography>
                  ) : (
                    <PatientCollectedData
                      patientProfileData={patientProfileResult.data || null}
                      patient={patient}
                    />
                  )
                ) : null}
              </Box>
            </Box>
            <Box sx={styles.gap} />
            <Box
              sx={{
                ...styles.chatContainer,
                width: chatOpen ? theme.spacing(56) : theme.spacing(10),
                [theme.breakpoints.down("desktop")]: {
                  width: chatOpen ? theme.spacing(40) : theme.spacing(7),
                },
              }}
            >
              <Box
                sx={{
                  ...styles.collapsedPaneContainer,
                  opacity: chatOpen ? 0 : 1,
                  pointerEvents: chatOpen ? "none" : "auto",
                }}
              >
                <Box sx={styles.messagesButtonContainer}>
                  <IconButton
                    icon={<MessagesIcon fill={theme.palette.grey[600]} />}
                    tooltip={t("common:expand")}
                    onClick={handleChatClick}
                  />
                  {patient.pending_hcw_message ? (
                    <Box sx={styles.newMessageDotIcon} />
                  ) : null}
                </Box>
                {nudgeData.numNudgesSinceLastClientMessage > 0 ? (
                  <>
                    <Typography variant="filterTypeTitleMini">
                      {nudgeData.numNudgesSinceLastClientMessage}
                    </Typography>
                    <Box sx={styles.nudgeDetailContainer}>
                      <Typography variant="nudgeSubtitleMini">
                        {t("nudge", {
                          count: nudgeData.numNudgesSinceLastClientMessage,
                        })}
                      </Typography>
                      <Typography variant="nudgeSubtitleMini2">
                        {formatDistance(0, nudgeData.minNudgeMessageAge, {
                          addSuffix: true,
                        })}
                      </Typography>
                    </Box>
                  </>
                ) : null}
              </Box>
              <ChatUx
                key={patient.uid}
                chatOpen={chatOpen}
                patient={patient}
                updateNudgeData={updateNudgeData}
              />
            </Box>
          </Box>
          <AddFollowupTaskDialog
            open={addFollowUpTaskDialogOpen}
            validateFields={handleAddFollowUpTaskDialogValidateFields}
            onClose={handleAddFollowUpTaskDialogClose}
            onSave={handleAddFollowUpTaskDialogSave}
          />
          <DeleteTaskDialog
            open={deleteTaskDialogInfo.open}
            onClose={handleDeleteTaskDialogClose}
            onDelete={handleDeleteTaskDialogAction}
          />
          <ErrorDialog
            open={errorDialogInfo.open}
            message={errorDialogInfo.message || ""}
            onClose={() => setErrorDialogInfo({ open: false })}
          />
          <UnsavedChangesDialog
            open={unsavedChangesDialogInfo.open}
            onClose={handleUnsavedChangesDialogClose}
            onDiscard={handleUnsavedChangesDialogDiscard}
          />
        </>
      ) : null}
    </Box>
  );
};

const styles = createStyleSheet({
  showActiveRow: {
    alignItems: "center",
    marginTop: 2,
  },
  container: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    px: 3,
    paddingBottom: 3,
    [theme.breakpoints.down("desktop")]: {
      px: 2,
      paddingBottom: 2,
    },
  },
  subContainer: {
    height: "100%",
    display: "flex",
    flexDirection: "row",
  },
  detailContainer: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  taskListContainer: {
    ...globalStyles.gapTop,
    borderRadius: 1,
    boxShadow: (theme) => theme.shadows[2],
    backgroundColor: "white",
    overflowY: "scroll",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    p: 3,
    [theme.breakpoints.down("desktop")]: {
      p: 2,
    },
  },
  iconGap: {
    marginRight: 1,
    [theme.breakpoints.down("desktop")]: {
      marginRight: 0.5,
    },
  },
  progressContainer: {
    ...globalStyles.gapTop,
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  gap: {
    width: (theme) => theme.spacing(3),
  },
  chatContainer: {
    transition: (theme) =>
      theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
      }),
    borderRadius: 1,
    boxShadow: (theme) => theme.shadows[2],
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
  },
  collapsedPaneContainer: {
    position: "absolute",
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    transition: (theme) =>
      theme.transitions.create("opacity", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
      }),
  },
  messagesButtonContainer: {
    ...globalStyles.gapTop,
    position: "relative",
    marginBottom: 2,
  },
  newMessageDotIcon: {
    backgroundColor: (theme) => theme.palette.newMessageDot.main,
    width: 11,
    height: 11,
    borderRadius: "50%",
    border: "1px solid white",
    position: "absolute",
    top: 6,
    right: 6,
    pointerEvents: "none",
  },
  nudgeDetailContainer: {
    display: "flex",
    flexDirection: "column",
    borderRadius: 2,
    backgroundColor: (theme) => alpha(theme.palette.common.black, 0.08),
    p: 0.5,
    width: (theme) => theme.spacing(7),
  },
});

// This component ensures that the Patient component is unmounted and then remounted
// when the selected Patient uid changes.
const PatientWrapper = () => {
  const params = useParams();
  const uid = params.uid || "";
  return <Patient key={uid} />;
};

export default PatientWrapper;
